import { MQ } from '@common/constants/mq.constant';
import { ITabItem } from '@common/general-ui/tabs/tabs.component';
import { comparePath } from '@common/utils/compare-path.util';
import { IRegisterModel } from '@src/api/model/register.model';
import React, { Dispatch, ReactNode, SetStateAction, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { css } from 'twin.macro';
import { RegisterFooter } from './register-footer.component';
import { RegisterHeaderV3, REGISTER_TABS } from './register-header.component';
import { ITabChangeChandler } from './register-tabs.component';

export type ITabChangeEventArgs = { prevTab: ITabItem; newTab: ITabItem };
export type ITabChangeEventHandler = (args: ITabChangeEventArgs) => void;

interface IProps {
    children?: ReactNode;
    onTabChange: ITabChangeEventHandler;
    totalPrice?: number;
    setTotalPrice?: Dispatch<SetStateAction<number>>;
    registerInfo: IRegisterModel;
    saveToRegister: (data: Partial<IRegisterModel>) => void;
}

export const AltRegisterLayoutV3 = ({
    children,
    onTabChange,
    totalPrice,
    registerInfo,
    saveToRegister,
}: IProps): JSX.Element => {
    const history = useLocation();
    const activeTab = useMemo(
        () => REGISTER_TABS.find((f) => comparePath(f.href?.toString() || '', history.pathname)) as ITabItem,
        [history.pathname],
    );
    const handleTabChange = useCallback<ITabChangeChandler>(
        (tab) => {
            onTabChange({ prevTab: activeTab, newTab: tab });
        },
        [activeTab, onTabChange],
    );
    return (
        <div css={styles.root}>
            <RegisterHeaderV3
                activeTab={activeTab}
                onTabChange={handleTabChange}
                totalPrice={totalPrice}
                registerInfo={registerInfo}
                saveToRegister={saveToRegister}
            />
            <div css={styles.panelContainer}>{children}</div>
            <RegisterFooter />
        </div>
    );
};

const styles = {
    root: css`
        display: flex;
        flex-direction: column;
        min-height: 100vh;
    `,
    panelContainer: css`
        width: 100%;
        flex: 1;
        max-width: 1300px;
        padding: 16px 16px 40px;
        margin: 0 auto;
        ${MQ.lg} {
            padding: 24px 24px 40px;
        }
    `,
};
